/**
 * `/accounts` — operator-facing list of portal_customers (OCP-166).
 *
 * The first page operator opens every morning to see who signed up.
 * Search by email/displayName/companyName + status filter (active /
 * suspended / pending email verification). Each row carries the
 * primary user email, signup referrer/ref, subscription count, MRR,
 * and Mail credit balance.
 *
 * Click → /accounts/:id detail view (OCP-167, lands as the next child
 * of the OCP-165 epic).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import {
  ConsumerSuspectedBadge,
  MissingBusinessCapacityBadge,
  MissingContactBadge,
} from './AdminAccountDetailPage';
import { fmtDate } from '../lib/format-time';

type AccountStatus = 'active' | 'suspended' | 'pending_email_verify';

interface AccountListItem {
  id: string;
  email: string;
  display_name: string | null;
  company_name: string | null;
  primary_user_email: string | null;
  primary_user_verified: boolean | null;
  signup_referrer: string | null;
  signup_ref: string | null;
  status: AccountStatus;
  subscription_count: number;
  mrr_eur_cents: number;
  mail_credit_balance: number;
  created_at: string;
  archived_at: string | null;
  // OCP-524 - no authorized responsible contact person on file. Such an account
  // works normally but cannot conclude a NEW contract; these are the backfill
  // targets for pre-clause customers.
  missing_responsible_contact: boolean;
  // OCP-534 - no Sec. 14 BGB business-capacity declaration (or no company name)
  // on file. Same semantics: the portal works, a NEW contract conclusion does
  // not. These are the backfill targets for pre-decision customers.
  missing_business_capacity: boolean;
  // OCP-534 - an operator has refused further contract conclusion here.
  consumer_suspected: boolean;
}

interface ListResponse {
  items: AccountListItem[];
  total: number;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending_email_verify', label: 'Pending email verify' },
  { value: 'suspended', label: 'Suspended' },
] as const;

const PAGE_SIZE = 50;

export function AdminAccountsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  // OCP-524 - backfill worklist filter.
  const [missingContact, setMissingContact] = useState(false);
  // OCP-534 - the two Präambel B worklists.
  const [missingCapacity, setMissingCapacity] = useState(false);
  const [consumerSuspected, setConsumerSuspected] = useState(false);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (status && status !== 'all') params.set('status', status);
    if (missingContact) params.set('missing_responsible_contact', 'true');
    if (missingCapacity) params.set('missing_business_capacity', 'true');
    if (consumerSuspected) params.set('consumer_suspected', 'true');
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    try {
      const res = await api<ListResponse>(
        `/v1/admin/accounts?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Load failed (HTTP ${err.status}).`);
      } else {
        setError('Load failed.');
      }
    } finally {
      setLoading(false);
    }
  }, [search, status, missingContact, missingCapacity, consumerSuspected, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reset to first page when search / status / any worklist filter changes.
  useEffect(() => {
    setOffset(0);
  }, [search, status, missingContact, missingCapacity, consumerSuspected]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Accounts</h1>
        <p className="mt-1 text-xs text-gray-500">
          Portal customers. Click a row for users / subscriptions / Mail
          usage / suspend + force-logout actions.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email, display name, company…"
          className="flex-1 min-w-[250px] rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={missingContact}
            onChange={(e) => setMissingContact(e.target.checked)}
            data-testid="filter-missing-responsible-contact"
          />
          No responsible contact
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={missingCapacity}
            onChange={(e) => setMissingCapacity(e.target.checked)}
            data-testid="filter-missing-business-capacity"
          />
          No business declaration
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={consumerSuspected}
            onChange={(e) => setConsumerSuspected(e.target.checked)}
            data-testid="filter-consumer-suspected"
          />
          Consumer suspected
        </label>
        <div className="ml-auto text-xs text-gray-500">
          {loading ? 'Loading…' : `${total} ${total === 1 ? 'account' : 'accounts'}`}
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          No accounts match. Stage-1 is invite-only — once you mint the
          first invite-code + the customer signs up, they appear here.
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Display name</th>
                <th className="px-3 py-2">Primary user</th>
                <th className="px-3 py-2">Referrer</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Business</th>
                <th className="px-3 py-2 text-right">Subs</th>
                <th className="px-3 py-2 text-right">MRR €</th>
                <th className="px-3 py-2 text-right">Mail credits</th>
                <th className="px-3 py-2">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                  <td className="px-3 py-2">
                    <Link
                      to={`/accounts/${a.id}`}
                      className="font-mono text-xs text-brand-500 hover:underline"
                    >
                      {a.email}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-sm">{a.display_name ?? <span className="text-gray-400">—</span>}</div>
                    {a.company_name && (
                      <div className="text-xs text-gray-500">{a.company_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {a.primary_user_email ?? <span className="text-gray-400">none</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {a.signup_referrer ?? <span className="text-gray-400">—</span>}
                    {a.signup_ref && (
                      <span className="ml-1 rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-[10px] text-gray-700 dark:text-gray-300">
                        ?ref={a.signup_ref}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={a.status} />
                  </td>
                  <td className="px-3 py-2">
                    {a.missing_responsible_contact ? (
                      <MissingContactBadge />
                    ) : (
                      <span className="text-xs text-gray-400">named</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {a.consumer_suspected ? (
                      <ConsumerSuspectedBadge />
                    ) : a.missing_business_capacity ? (
                      <MissingBusinessCapacityBadge />
                    ) : (
                      <span className="text-xs text-gray-400">declared</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {a.subscription_count}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {(a.mrr_eur_cents / 100).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {a.mail_credit_balance.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {fmtDate(a.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            ← Prev
          </button>
          <div className="text-xs text-gray-500">
            Showing {offset + 1}–{Math.min(offset + items.length, total)} of {total}
          </div>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: AccountStatus }) {
  const config = {
    active: { label: 'active', cls: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
    suspended: { label: 'suspended', cls: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    pending_email_verify: {
      label: 'pending verify',
      cls: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
    },
  }[status];
  return (
    <span className={`rounded border px-2 py-0.5 text-xs ${config.cls}`}>
      {config.label}
    </span>
  );
}
